import type { GalleryComment, GalleryItem } from './gallery.api';

export type GalleryStatus = 'ACTIVE' | 'SELECTION_SENT' | 'EXPIRED';

export function galleryStatus(
  gallery: { expiresAt?: string | null; selection: { sentAt?: string | null; locked: boolean } },
  now: Date = new Date()
): GalleryStatus {
  if (gallery.expiresAt && new Date(gallery.expiresAt).getTime() <= now.getTime()) {
    return 'EXPIRED';
  }
  return gallery.selection.locked && gallery.selection.sentAt ? 'SELECTION_SENT' : 'ACTIVE';
}

type ItemLike = Pick<GalleryItem, 'uuid'> & Partial<Pick<GalleryItem, 'thumb800Url' | 'webUrl'>>;

export function allItems<T extends ItemLike>(gallery: { sections: Array<{ items: T[] }> }): T[] {
  return gallery.sections.flatMap((section) => section.items);
}

function findCover<T extends ItemLike>(gallery: {
  coverItemUuid?: string | null;
  sections: Array<{ items: T[] }>;
}): T | undefined {
  return gallery.coverItemUuid
    ? allItems(gallery).find((item) => item.uuid === gallery.coverItemUuid)
    : undefined;
}

// List cards are small: the 800 px thumbnail of `Gallery.cover`.
export function summaryCoverUrl(gallery: {
  cover?: Pick<GalleryItem, 'uuid' | 'thumb800Url'> | null;
}): string | null {
  return gallery.cover?.thumb800Url ?? null;
}

// The gallery page's full-width cover: the web copy, else the thumbnail. `Gallery.cover` (an
// uploaded cover or the picked item) wins; without it the picked item is found among the items.
export function coverImageUrl(gallery: {
  coverItemUuid?: string | null;
  cover?: { webUrl?: string | null; thumb800Url?: string | null } | null;
  sections: Array<{ items: ItemLike[] }>;
}): string | null {
  const cover = gallery.cover ?? findCover(gallery);
  return cover?.webUrl ?? cover?.thumb800Url ?? null;
}

// `list` with `moving` put where `target` is (after it when moving forward, before it otherwise).
export function moveUuid(list: string[], moving: string, target: string): string[] {
  const from = list.indexOf(moving);
  const to = list.indexOf(target);
  if (from < 0 || to < 0 || from === to) {
    return list;
  }
  const next = list.filter((uuid) => uuid !== moving);
  next.splice(to, 0, moving);
  return next;
}

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

// The access stops at the end of the chosen day in the Provider's time zone.
export function expiresAtFromDate(date: string): string | null {
  const match = DATE_PATTERN.exec(date.trim());
  if (!match) {
    return null;
  }
  const [, year, month, day] = match.map(Number);
  return new Date(year, month - 1, day, 23, 59, 59).toISOString();
}

export function dateFromExpiresAt(expiresAt: string | null | undefined): string {
  if (!expiresAt) {
    return '';
  }
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function parseSelectionLimit(
  text: string
): { ok: true; value: number | null } | { ok: false } {
  const value = text.trim();
  if (!value) {
    return { ok: true, value: null };
  }
  if (!/^\d+$/.test(value) || Number(value) < 1) {
    return { ok: false };
  }
  return { ok: true, value: Number(value) };
}

export function commentsByItem(
  comments: Array<Pick<GalleryComment, 'uuid' | 'itemUuid'> & Partial<GalleryComment>>
): Map<string, typeof comments> {
  const grouped = new Map<string, typeof comments>();
  for (const comment of comments) {
    grouped.set(comment.itemUuid, [...(grouped.get(comment.itemUuid) ?? []), comment]);
  }
  return grouped;
}
